/* Add presentation only. Judge.me remains the source of review data and actions. */
(() => {
  const defaultTabHandled = new WeakSet();
  const selectDefaultTab = (widget) => {
    if (defaultTabHandled.has(widget)) return;
    const productTab = widget.querySelector('#jdgm-tab_product_reviews');
    const storeTab = widget.querySelector('#jdgm-tab_shop_reviews');
    const productCount = productTab?.textContent.match(/\(([\d,]+)\)/);
    const storeCount = storeTab?.textContent.match(/\(([\d,]+)\)/);
    if (!productCount || !storeCount) return;
    // Wait for the native tabs to initialize, then choose only once. A later
    // customer selection must never be overridden by a widget re-render.
    if (!widget.querySelector('[role="tabpanel"]')) return;
    defaultTabHandled.add(widget);
    if (Number(productCount[1].replaceAll(',', '')) === 0 &&
        Number(storeCount[1].replaceAll(',', '')) > 0 &&
        storeTab.getAttribute('aria-selected') !== 'true') storeTab.click();
  };
  // Fetch rating totals, not all review pages. Keep store and item data separate.
  const storeRequests = new Map();
  const validSummary = (data, total) => data && data.total === total &&
    Array.isArray(data.counts) && data.counts.length === 5 &&
    data.counts.every(n => Number.isInteger(n) && n >= 0) &&
    data.counts.reduce((a, b) => a + b, 0) === total;
  const loadStoreSummary = (total) => {
    const shop = window.Shopify?.shop;
    if (!shop) return Promise.resolve(null);
    const key = `pos-store-ratings-v2:${shop}:${total}`;
    try {
      const cached = JSON.parse(sessionStorage.getItem(key));
      if (cached && Date.now() - cached.time < 900000 && validSummary(cached.data, total)) return Promise.resolve(cached.data);
    } catch (_) { /* Storage may be disabled. */ }
    if (storeRequests.has(key)) return storeRequests.get(key);
    const request = (async () => {
      const counts = [0, 0, 0, 0, 0];
      // Sequential, bounded requests avoid bursts against Judge.me's API.
      for (let rating = 5; rating >= 1 && counts.reduce((a, b) => a + b, 0) < total; rating--) {
        const url = new URL('https://cdn.judge.me/reviews/all_reviews_js_based');
        url.search = new URLSearchParams({ widget_type: 'review-widget', page: 1,
          filter_rating: rating, review_type: 'shop-reviews', sort_by: 'created_at',
          sort_dir: 'desc', widget_theme: 'standard', shop_domain: shop, platform: 'shopify' });
        const response = await fetch(url, { signal: AbortSignal.timeout(15000) });
        if (!response.ok) throw new Error('Store ratings unavailable');
        const result = await response.json();
        const count = Number(result.pagination?.total_count);
        if (!Number.isInteger(count) || count < 0 || !Array.isArray(result.reviews) ||
            result.reviews.some(review => Number(review.rating) !== rating || review.is_shop_review !== true)) return null;
        counts[rating - 1] = count;
      }
      const data = { total, counts };
      if (!validSummary(data, total)) return null;
      try { sessionStorage.setItem(key, JSON.stringify({ time: Date.now(), data })); } catch (_) { /* Optional cache. */ }
      return data;
    })().catch(() => null);
    storeRequests.set(key, request);
    return request;
  };
  // Last verified Judge.me store histogram, a bounded outage fallback only.
  // Never apply to another shop or a different review total; refresh from API.
  const verifiedStoreSnapshot = {
    shop: 'udfdcp-ev.myshopify.com', verifiedAt: '2026-10-26T03:44:51.978929+00:00',
    total: 93, counts: [0, 0, 0, 3, 90]
  };
  const summaries = new WeakMap();
  const updateStoreSummary = (widget) => {
    const host = widget.querySelector('.jm-review-widget-minimal-header > .jm-cluster > .jm-stack:first-child');
    if (!host) return;
    let state = summaries.get(widget);
    if (!state || !state.node.isConnected) {
      const node = document.createElement('div');
      node.className = 'gp-rating-card';
      host.append(node);
      state = { node, store: null, requestedTotal: null, key: '' };
      summaries.set(widget, state);
    }
    const isStore = widget.querySelector('#jdgm-tab_shop_reviews')?.getAttribute('aria-selected') === 'true';
    const total = Number(widget.dataset.shopReviewsCount);
    let data;
    if (isStore) {
      data = state.store;
      if (!data && window.Shopify?.shop === verifiedStoreSnapshot.shop &&
          Date.now() - Date.parse(verifiedStoreSnapshot.verifiedAt) < 86400000 &&
          validSummary(verifiedStoreSnapshot, total)) data = verifiedStoreSnapshot;
      if (state.requestedTotal !== total && Number.isInteger(total) && total >= 0) {
        state.requestedTotal = total;
        loadStoreSummary(total).then(result => {
          if (result && state.node.isConnected) {
            state.store = result;
          }
          state.failed = !result;
          if (state.node.isConnected) updateStoreSummary(widget);
        });
      }
    } else {
      const item = window.jdgm?.data?.reviewWidget?.[widget.dataset.productId];
      if (item && Array.isArray(item.histogram)) {
        const counts = [0, 0, 0, 0, 0];
        item.histogram.forEach(row => { if (row.rating >= 1 && row.rating <= 5) counts[row.rating - 1] = Number(row.frequency); });
        const candidate = { total: Number(item.number_of_reviews), counts };
        if (validSummary(candidate, candidate.total)) data = candidate;
      }
    }
    const key = JSON.stringify([isStore, data, total, state.failed]);
    if (state.key === key) return;
    state.key = key;
    widget.classList.add('gp-reference-reviews');
    const heading = document.createElement('h3');
    heading.textContent = isStore ? 'Reviews for this store' : 'Reviews for this item';
    const content = [heading];
    if (data) {
      const average = data.total ? data.counts.reduce((sum, n, i) => sum + n * (i + 1), 0) / data.total : 0;
      const rating = document.createElement('div');
      rating.className = 'gp-rating-card__average';
      const score = document.createElement('strong');
      score.textContent = data.total ? average.toFixed(2) : '—';
      const label = document.createElement('div');
      const stars = document.createElement('span');
      stars.className = 'gp-review-summary-stars';
      stars.setAttribute('aria-label', `${average.toFixed(2)} out of 5 stars`);
      stars.textContent = '★★★★★';
      const fill = document.createElement('span');
      fill.textContent = '★★★★★';
      stars.append(fill);
      stars.style.setProperty('--gp-review-fill', `${average / 5 * 100}%`);
      const count = document.createElement('span');
      count.textContent = `${data.total.toLocaleString('en-US')} ${data.total === 1 ? 'Review' : 'Reviews'}`;
      label.append(stars, count);
      rating.append(score, label);
      content.push(rating);
      const histogram = document.createElement('div');
      histogram.className = 'gp-rating-card__histogram';
      for (let i = 5; i >= 1; i--) {
        const percent = data.total ? data.counts[i - 1] / data.total * 100 : 0;
        const row = document.createElement('div');
        row.className = 'gp-rating-card__bar';
        row.setAttribute('aria-label', `${i} stars: ${data.counts[i - 1]} reviews`);
        const name = document.createElement('span');
        name.textContent = `${i} Star`;
        const track = document.createElement('span');
        track.className = 'gp-rating-card__track';
        const bar = document.createElement('span');
        bar.style.width = `${percent}%`;
        track.append(bar);
        const value = document.createElement('span');
        value.textContent = percent > 0 && percent < 1 ? '<1%' : `${Math.round(percent)}%`;
        row.append(name, track, value);
        histogram.append(row);
      }
      content.push(histogram);
    } else {
      const loading = document.createElement('p');
      loading.setAttribute('role', 'status');
      loading.textContent = isStore ? (state.failed ? `${total} store reviews · Ratings temporarily unavailable.` : `${total} store reviews · Loading ratings…`) : 'Loading ratings…';
      content.push(loading);
    }
    const action = document.createElement('button');
    action.type = 'button';
    action.className = 'gp-rating-card__write';
    action.textContent = 'Write a Review';
    action.addEventListener('click', () => {
      widget.querySelector('.jm-action-buttons__button, .jm-no-reviews-state button')?.click();
    });
    content.push(action);
    state.node.replaceChildren(...content);
  };
  const enhance = (widget) => {
    selectDefaultTab(widget);
    updateStoreSummary(widget);
    const controls = widget.querySelector('.jm-review-widget-sidebar-header__tabs .jdgm-tablist__content, .jm-review-widget-minimal-header__tabs .jdgm-tablist__content');
    if (controls && !controls.querySelector('.gp-review-list-title')) {
      const title = document.createElement('h2');
      title.className = 'gp-review-list-title';
      title.textContent = 'Top reviews';
      controls.prepend(title);
    }
    const rows = [...widget.querySelectorAll('.jm-histogram-row')];
    if (controls && rows.length === 5) {
      let filters = controls.querySelector('.gp-review-rating-filters');
      if (!filters) {
        filters = document.createElement('div');
        filters.className = 'gp-review-rating-filters';
        filters.setAttribute('role', 'group');
        filters.setAttribute('aria-label', 'Filter reviews by rating');
        for (const rating of [0, 5, 4, 3, 2, 1]) {
          const button = document.createElement('button');
          button.type = 'button';
          button.dataset.rating = rating;
          button.addEventListener('click', () => {
            if (rating === 0) widget.querySelector('.jm-filter-panel__clear-btn')?.click();
            else [...widget.querySelectorAll('.jm-histogram-row')].find(row => Number(row.querySelector('.jm-histogram-row__rating')?.textContent) === rating)?.click();
            requestAnimationFrame(() => enhance(widget));
          });
          filters.append(button);
        }
        controls.querySelector('.gp-review-list-title').after(filters);
      }
      const counts = new Map(rows.map(row => [Number(row.querySelector('.jm-histogram-row__rating')?.textContent), row.querySelector('.jm-histogram-row__frequency')?.textContent.trim() || '0']));
      const active = Number(widget.querySelector('input[name="rating-filter"]:checked')?.value || 0);
      for (const button of filters.children) {
        const rating = Number(button.dataset.rating);
        const count = rating ? counts.get(rating) : widget.querySelector('.jm-average-rating-display .jm-text:last-of-type')?.textContent.trim().replace(/\s+reviews?$/i, '');
        const label = `${rating ? rating + ' Star' : 'All Reviews'}${count ? ' (' + count + ')' : ''}`;
        if (button.textContent !== label) button.textContent = label;
        const pressed = String(active === rating);
        if (button.getAttribute('aria-pressed') !== pressed) button.setAttribute('aria-pressed', pressed);
      }
    } else controls?.querySelector('.gp-review-rating-filters')?.remove();
    const average = widget.querySelector('.jm-average-rating-display');
    if (!average) return;
    const score = Number(average.querySelector('.jm-text')?.textContent.trim().replace(',', '.'));
    let stars = average.querySelector('.gp-review-summary-stars');
    if (!Number.isFinite(score) || score <= 0 || score > 5) {
      stars?.remove();
      return;
    }
    if (!stars) {
      stars = document.createElement('span');
      stars.className = 'gp-review-summary-stars';
      stars.setAttribute('aria-hidden', 'true');
      stars.textContent = '★★★★★';
      const fill = document.createElement('span');
      fill.textContent = '★★★★★';
      stars.append(fill);
      average.append(stars);
    }
    const fill = `${score / 5 * 100}%`;
    if (stars.style.getPropertyValue('--gp-review-fill') !== fill) stars.style.setProperty('--gp-review-fill', fill);
  };
  const observed = new WeakSet();
  const scan = () => document.querySelectorAll('.jdgm-review-widget').forEach((widget) => {
    if (observed.has(widget)) return;
    observed.add(widget);
    const respectTabChoice = (event) => {
      if (event.isTrusted && event.target.closest('[role="tab"], .jdgm-tab-dropdown__select')) {
        defaultTabHandled.add(widget);
      }
    };
    widget.addEventListener('click', respectTabChoice, true);
    widget.addEventListener('change', respectTabChoice, true);
    let pending = false;
    new MutationObserver(() => {
      if (pending) return;
      pending = true;
      requestAnimationFrame(() => { pending = false; if (widget.isConnected) enhance(widget); });
    }).observe(widget, { childList: true, subtree: true, characterData: true, attributes: true, attributeFilter: ['aria-selected'] });
    widget.addEventListener('change', () => requestAnimationFrame(() => enhance(widget)));
    enhance(widget);
  });
  new MutationObserver(scan).observe(document.body, { childList: true, subtree: true });
  scan();
})();
